import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Wallet, CreditCard, Bank, CurrencyDollar, PencilSimple, FloppyDisk, X, Money,
} from "@phosphor-icons/react";

const ICONS = {
  Wallet, CreditCard, Bank, CurrencyDollar, Banknote: Money,
};

/**
 * Iter119 — Admin: gestione dinamica dei Metodi di Pagamento.
 *
 * Elenca tutti i metodi (attivi e disattivi) con toggle enabled + editor
 * inline per etichetta/istruzioni/icona/order. Il `type` è read-only
 * (è il discriminante tecnico della logica di acquisto).
 */
export default function PaymentMethodsAdmin() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  // Iter120: messaggio globale mostrato al cliente finale nel checkout pubblico.
  const [clientMessage, setClientMessage] = useState("");
  const [messageDirty, setMessageDirty] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [feePct, setFeePct] = useState("30");
  const [feeDirty, setFeeDirty] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [shareOn, setShareOn] = useState(true);
  const [savingShare, setSavingShare] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/payment-methods"),
      api.get("/payment-settings"),
      api.get("/admin/settings"),
    ])
      .then(([mRes, sRes, aRes]) => {
        setMethods(mRes.data || []);
        setShareOn(aRes.data?.enable_share_with_client !== false);
        setClientMessage(sRes.data?.client_payment_message || "");
        setMessageDirty(false);
        const r = Number(sRes.data?.housing_fee_rate);
        setFeePct(String(Math.round((Number.isFinite(r) ? r : 0.3) * 10000) / 100));
        setFeeDirty(false);
      })
      .catch(() => toast.error("Errore caricamento configurazione pagamenti"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveFee = async () => {
    const pct = Number(String(feePct).replace(",", "."));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Inserisci una percentuale tra 0 e 100");
      return;
    }
    setSavingFee(true);
    try {
      await api.put("/payment-settings", { housing_fee_rate: Math.round(pct * 100) / 10000 });
      toast.success("Contributo salvato");
      setFeeDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingFee(false);
    }
  };

  const saveMessage = async () => {
    setSavingMessage(true);
    try {
      await api.put("/payment-settings", { client_payment_message: clientMessage });
      toast.success("Messaggio salvato");
      setMessageDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingMessage(false);
    }
  };

  const toggleShare = async () => {
    setSavingShare(true);
    try {
      await api.put("/admin/settings", { enable_share_with_client: !shareOn });
      toast.success(shareOn ? "Condivisione con cliente disattivata: resta solo il Plafond" : "Condivisione con cliente attivata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingShare(false);
    }
  };

  const toggle = async (m) => {
    try {
      await api.put(`/payment-methods/${m._id}`, { enabled: !m.enabled });
      toast.success(m.enabled ? `"${m.name}" disattivato` : `"${m.name}" attivato`);
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  // Iter136: aggiornamento generico di un singolo campo visibilità.
  const patchVisibility = async (m, field) => {
    try {
      await api.put(`/payment-methods/${m._id}`, { [field]: !m[field] });
      toast.success("Visibilità aggiornata");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento visibilità");
    }
  };

  const startEdit = (m) => {
    setEditingId(m._id);
    setDraft({
      name: m.name || "",
      instructions: m.instructions || "",
      icon: m.icon || "CreditCard",
      order: m.order ?? 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const save = async (m) => {
    try {
      await api.put(`/payment-methods/${m._id}`, draft);
      toast.success("Metodo aggiornato");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-8 text-center">Caricamento…</div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-methods-admin">
      <div className="text-sm text-slate-600">
        Attiva o disattiva i metodi di pagamento disponibili nel marketplace.
        <br />
        <span className="text-[11px] text-slate-500">
          Nota: il tipo tecnico (`stripe`, `plafond`, ecc.) non è modificabile — cambia solo etichetta, icona e istruzioni.
        </span>
      </div>

      {/* Iter120: messaggio globale mostrato al CLIENTE FINALE nel checkout pubblico */}
      <div className="p-4 rounded-lg border-2 border-violet-200 bg-violet-50/40 flex items-start justify-between gap-4" data-testid="share-with-client-box">
        <div>
          <div className="font-semibold text-sm text-violet-900">Condividi con cliente</div>
          <div className="text-[11px] text-violet-800 mt-0.5">
            {shareOn
              ? "Il Pro può condividere la pratica con il cliente finale, che paga con i metodi attivi per il Cliente."
              : "Spento: il Pro può acquistare solo tramite Plafond. I metodi per il cliente sono disattivati."}
          </div>
        </div>
        <Switch checked={shareOn} disabled={savingShare} onCheckedChange={toggleShare} data-testid="toggle-enable_share_with_client" />
      </div>
      <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50/40" data-testid="client-payment-message-box">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="font-semibold text-sm text-indigo-900">
              💬 Messaggio per il cliente finale
            </div>
            <div className="text-[11px] text-indigo-800 mt-0.5">
              Verrà mostrato sopra i pulsanti di pagamento nella pagina pubblica <code>/pay/&lt;id&gt;</code>.
              Non è visibile al professionista che acquista dal proprio plafond.
            </div>
          </div>
          {messageDirty && (
            <Button
              size="sm"
              onClick={saveMessage}
              disabled={savingMessage}
              className="shrink-0"
              data-testid="client-payment-message-save"
            >
              <FloppyDisk size={14} className="mr-1" />
              {savingMessage ? "Salvo…" : "Salva"}
            </Button>
          )}
        </div>
        <Textarea
          rows={3}
          value={clientMessage}
          onChange={(e) => { setClientMessage(e.target.value.slice(0, 5000)); setMessageDirty(true); }}
          maxLength={5000}
          placeholder="Es. Grazie di aver scelto Housetag! Riceverai una fattura via email entro 24 ore dal pagamento. Per assistenza scrivi a supporto@housetag.it"
          className="text-sm bg-white"
          data-testid="client-payment-message-textarea"
        />
        <div className="text-[10px] text-slate-500 mt-1 text-right">
          {clientMessage.length}/5000 caratteri
        </div>
      </div>

      <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50/40" data-testid="housing-fee-box">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="font-semibold text-sm text-indigo-900">Contributo di fatturazione Housetag (%)</div>
            <div className="text-[11px] text-indigo-800 mt-0.5">
              Percentuale del prezzo pro aggiunta alle pratiche condivise con il cliente. Si applica ai nuovi acquisti.
            </div>
          </div>
          {feeDirty && (
            <Button size="sm" onClick={saveFee} disabled={savingFee} className="shrink-0" data-testid="housing-fee-save">
              <FloppyDisk size={14} className="mr-1" />
              {savingFee ? "Salvo…" : "Salva"}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={feePct}
            onChange={(e) => { setFeePct(e.target.value); setFeeDirty(true); }}
            className="w-32 text-sm bg-white"
            data-testid="housing-fee-input"
          />
          <span className="text-sm text-slate-600">%</span>
        </div>
      </div>

      <div className="space-y-2">
        {methods.map((m) => {
          const Icon = ICONS[m.icon] || CreditCard;
          const isEditing = editingId === m._id;
          const isPlafond = m.type === "plafond";
          const locked = !shareOn && !isPlafond;
          const enabledShown = !shareOn && isPlafond ? true : !!m.enabled;
          return (
            <div
              key={m._id}
              className={`p-3 rounded-lg border-2 transition-all ${enabledShown && !locked ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60 opacity-60"}`}
              data-testid={`pm-row-${m.type}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${m.enabled ? "bg-emerald-500 text-white" : "bg-slate-400 text-white"}`}>
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  {!isEditing ? (
                    <>
                      <div className="font-semibold text-sm text-slate-900 truncate">
                        {m.name}
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono uppercase tracking-wider">
                          {m.type}
                        </span>
                      </div>
                      {m.instructions && (
                        <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                          {m.instructions}
                        </div>
                      )}
                    </>
                  ) : (
                    <Input
                      value={draft.name || ""}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      className="text-sm font-semibold"
                      data-testid={`pm-name-input-${m.type}`}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={enabledShown}
                    disabled={!shareOn}
                    onCheckedChange={() => toggle(m)}
                    data-testid={`pm-toggle-${m.type}`}
                  />
                  {/* Iter136 — Visibilità target: Pro / Cliente */}
                  <div className="flex items-center gap-2 pl-2 ml-1 border-l text-[10px] text-slate-700">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <Switch
                        checked={m.visible_to_pro !== false}
                        disabled={locked}
                        onCheckedChange={() => patchVisibility(m, "visible_to_pro")}
                        data-testid={`pm-vis-pro-${m.type}`}
                      />
                      <span>Pro</span>
                    </label>
                    <label className={`flex items-center gap-1 ${shareOn ? "cursor-pointer" : "opacity-50"}`}>
                      <Switch
                        checked={m.visible_to_client !== false}
                        disabled={!shareOn}
                        onCheckedChange={() => patchVisibility(m, "visible_to_client")}
                        data-testid={`pm-vis-client-${m.type}`}
                      />
                      <span>Cliente</span>
                    </label>
                  </div>
                  {!isEditing ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(m)}
                      data-testid={`pm-edit-${m.type}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => save(m)}
                        data-testid={`pm-save-${m.type}`}
                      >
                        <FloppyDisk size={14} className="mr-1" /> Salva
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEdit}
                        data-testid={`pm-cancel-${m.type}`}
                      >
                        <X size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-3 pl-13 space-y-2 border-t pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5 block">
                        Icona (phosphor)
                      </label>
                      <select
                        value={draft.icon || "CreditCard"}
                        onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                        className="w-full h-9 border rounded px-2 text-sm bg-white"
                        data-testid={`pm-icon-${m.type}`}
                      >
                        {Object.keys(ICONS).map(k => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5 block">
                        Ordine
                      </label>
                      <Input
                        type="number"
                        value={draft.order ?? 0}
                        onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
                        className="text-sm"
                        data-testid={`pm-order-${m.type}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5 block">
                      Istruzioni per il PRO (opzionale)
                    </label>
                    <Textarea
                      rows={2}
                      value={draft.instructions || ""}
                      onChange={(e) => setDraft({ ...draft, instructions: e.target.value.slice(0, 2000) })}
                      maxLength={2000}
                      className="text-sm"
                      data-testid={`pm-instructions-${m.type}`}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
